import * as THREE from 'three'
import CANNON from 'cannon'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import Smoke from './Smoke.js'

export default class Car
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.objects = _options.objects
        this.physics = _options.physics
        this.shadows = _options.shadows
        this.materials = _options.materials
        this.controls = _options.controls
        this.sounds = _options.sounds
        this.renderer = _options.renderer
        this.camera = _options.camera
        this.debug = _options.debug
        this.config = _options.config

        // Set up
        this.container = new THREE.Object3D()
        this.position = new THREE.Vector3()

        // Debug
        if(this.debug)
        {
            this.debugFolder = this.debug.addFolder('car')
            // this.debugFolder.open()
        }

        this.setModels()
        this.setAnimations()  // NEW: Setup animations
        this.setMovement()
        this.setChassis()
        this.setAntena()
        this.setBackLights()
        this.setWheels()
        this.setTransformControls()
        this.setShootingBall()
        this.setKlaxon()
        this.setSmoke()
    }

    setModels()
    {
        this.models = {}

        // Cyber truck
        if(this.config.cyberTruck)
        {
            this.models.chassis = this.resources.items.carCyberTruckChassis
            this.models.antena = this.resources.items.carCyberTruckAntena
            this.models.backLightsBrake = this.resources.items.carCyberTruckBackLightsBrake
            this.models.backLightsReverse = this.resources.items.carCyberTruckBackLightsReverse
            this.models.wheel = this.resources.items.carCyberTruckWheel
        }

        // Default
        else
        {
            this.models.chassis = this.resources.items.carDefaultChassis
            this.models.antena = this.resources.items.carDefaultAntena
            this.models.backLightsBrake = this.resources.items.carDefaultBackLightsBrake
            this.models.backLightsReverse = this.resources.items.carDefaultBackLightsReverse
            this.models.wheel = this.resources.items.carDefaultWheel
        }
    }

    setAnimations()
    {
        this.animations = {}
        
        // IMPORTANT: We'll create the mixer AFTER we set up the chassis object
        // Store animation clips for later
        this.animations.clips = this.models.chassis.animations || []
        
        if(this.animations.clips.length > 0)
        {
            console.log('Animation clips found:', this.animations.clips.map(c => c.name))
        }
        else
        {
            console.warn('No animations found in chassis model')
        }
    }

    setupAnimationMixer()
    {
        // This is called AFTER setChassis() creates the chassis.object
        if(this.animations.clips.length === 0) return

        // Create animation mixer on the actual chassis object (not the original model)
        this.animations.mixer = new THREE.AnimationMixer(this.chassis.object)
        
        // Store all animation actions
        this.animations.actions = {}
        
        // Map all animations from the stored clips
        this.animations.clips.forEach((clip) => {
            const action = this.animations.mixer.clipAction(clip)
            this.animations.actions[clip.name] = action
            
            console.log(`Loaded animation: ${clip.name}`)
        })

        // Specific actions for steering - can use multiple animations for each direction
        this.animations.steerLeft = [
            this.animations.actions['SL'],
            this.animations.actions['Action.002']
        ].filter(action => action !== undefined)
        
        this.animations.steerRight = [
            this.animations.actions['SR'],
            this.animations.actions['Action.003']
        ].filter(action => action !== undefined)
        
        // ============================================
        // IDLE ANIMATION SYSTEM (COMMENTED OUT)
        // This code demonstrates how to create idle head movements:
        // - Default animation plays continuously (looking forward)
        // - Quick glance animations trigger every ~5 seconds for < 1 second
        // - Smooth fades between animations for natural movement
        // ============================================
        
        // Separate default (looking forward) from quick glances
        // this.animations.idleDefault = this.animations.actions['def.001']  // Looking forward - plays most of the time
        // this.animations.idleGlances = [
        //     this.animations.actions['Action.002'],
        //     this.animations.actions['Action.003']
        // ].filter(action => action !== undefined)

        // Configure default idle (looking forward) - loops continuously
        // if(this.animations.idleDefault)
        // {
        //     this.animations.idleDefault.setLoop(THREE.LoopRepeat)
        //     this.animations.idleDefault.clampWhenFinished = false
        //     this.animations.idleDefault.timeScale = 1
        // }

        // Configure glance animations - quick, one-time glances
        // this.animations.idleGlances.forEach(action => {
        //     if(action)
        //     {
        //         action.setLoop(THREE.LoopOnce)  // Play once then return
        //         action.clampWhenFinished = true
        //         action.timeScale = 1.5  // Slightly faster for quick glances
        //     }
        // })

        // Configure steering animations
        const allSteerActions = [
            ...this.animations.steerLeft, 
            ...this.animations.steerRight
        ]
        
        allSteerActions.forEach(action => {
            if(action)
            {
                action.setLoop(THREE.LoopOnce)
                action.clampWhenFinished = true
            }
        })

        // Explicitly stop def.001 (idle animation) from playing
        if(this.animations.actions['def.001'])
        {
            this.animations.actions['def.001'].stop()
            this.animations.actions['def.001'].setEffectiveWeight(0)
        }

        // Idle animation state
        // this.animations.currentGlance = null
        // this.animations.idleTimer = 0
        // this.animations.idleInterval = 5000 + Math.random() * 1000 // 5-6 seconds between glances
        // this.animations.glanceDuration = 800 // Quick glance duration in ms (< 1 second)
        // this.animations.glanceStartTime = 0
        // this.animations.isGlancing = false
        
        // Start the default idle animation (looking forward) immediately
        // if(this.animations.idleDefault)
        // {
        //     this.animations.idleDefault.reset()
        //     this.animations.idleDefault.setEffectiveWeight(1)
        //     this.animations.idleDefault.fadeIn(0.5)  // Smooth fade in
        //     this.animations.idleDefault.play()
        //     console.log('Started default idle animation (looking forward)')
        // }

        // Steering state
        this.animations.currentSteerWeight = 0
        this.animations.targetSteerWeight = 0
        this.animations.steerSmoothing = 0.1

        console.log('Car animations initialized:', Object.keys(this.animations.actions))
    }

    updateAnimations()
    {
        if(!this.animations.mixer) return

        // Update animation mixer
        this.animations.mixer.update(this.time.delta * 0.001)

        // Handle steering animations based on controls
        if(this.controls && (this.animations.steerLeft || this.animations.steerRight))
        {
            // Determine target steering weight (-1 = full left, 0 = center, 1 = full right)
            if(this.controls.actions.left && !this.controls.actions.right)
            {
                this.animations.targetSteerWeight = -1
            }
            else if(this.controls.actions.right && !this.controls.actions.left)
            {
                this.animations.targetSteerWeight = 1
            }
            else
            {
                this.animations.targetSteerWeight = 0
            }

            // Smoothly interpolate to target
            this.animations.currentSteerWeight += 
                (this.animations.targetSteerWeight - this.animations.currentSteerWeight) * 
                this.animations.steerSmoothing

            // Apply steering animations
            if(this.animations.steerLeft.length > 0)
            {
                const leftWeight = Math.max(0, -this.animations.currentSteerWeight)
                
                this.animations.steerLeft.forEach(action => {
                    action.setEffectiveWeight(leftWeight)
                    
                    if(leftWeight > 0 && !action.isRunning())
                    {
                        action.reset().play()
                    }
                    else if(leftWeight === 0 && action.isRunning())
                    {
                        // Stop animation when weight reaches 0
                        action.stop()
                    }
                })
            }

            if(this.animations.steerRight.length > 0)
            {
                const rightWeight = Math.max(0, this.animations.currentSteerWeight)
                
                this.animations.steerRight.forEach(action => {
                    action.setEffectiveWeight(rightWeight)
                    
                    if(rightWeight > 0 && !action.isRunning())
                    {
                        action.reset().play()
                    }
                    else if(rightWeight === 0 && action.isRunning())
                    {
                        // Stop animation when weight reaches 0
                        action.stop()
                    }
                })
            }
        }

        // ============================================
        // IDLE ANIMATION UPDATE LOGIC (COMMENTED OUT)
        // This demonstrates:
        // - How to check if steering is active to avoid conflicts
        // - Timer-based animation triggering (every ~5 seconds)
        // - Smooth blending between animations using setEffectiveWeight()
        // - Fade in/out for natural transitions
        // - Short-duration animations (< 1 second) for quick glances
        // ============================================
        
        // Handle idle animations (default looking forward + occasional glances)
        // Check if steering is active
        // const isSteering = this.controls && 
        //     (this.controls.actions.left || this.controls.actions.right) &&
        //     Math.abs(this.animations.currentSteerWeight) > 0.1

        // Only update idle animations when not steering
        // if(!isSteering)
        // {
        //     // Ensure default idle (looking forward) is always playing
        //     if(this.animations.idleDefault)
        //     {
        //         if(!this.animations.idleDefault.isRunning())
        //         {
        //             this.animations.idleDefault.reset()
        //             this.animations.idleDefault.fadeIn(0.5)
        //             this.animations.idleDefault.play()
        //         }
        //         
        //         // Adjust weight based on whether we're glancing
        //         if(this.animations.isGlancing)
        //         {
        //             // Blend with glance animation
        //             this.animations.idleDefault.setEffectiveWeight(0.3)
        //         }
        //         else
        //         {
        //             // Full weight when just looking forward
        //             this.animations.idleDefault.setEffectiveWeight(1)
        //         }
        //     }

        //     // Handle quick glance animations
        //     if(this.animations.isGlancing && this.animations.currentGlance)
        //     {
        //         // Check if glance duration has passed
        //         const glanceElapsed = this.time.elapsed - this.animations.glanceStartTime
        //         
        //         if(glanceElapsed >= this.animations.glanceDuration)
        //         {
        //             // Glance finished, fade it out and return to default
        //             this.animations.currentGlance.fadeOut(0.4)  // Smooth fade out
        //             this.animations.currentGlance.stop()
        //             this.animations.currentGlance = null
        //             this.animations.isGlancing = false
        //             
        //             // Fade default back to full weight
        //             if(this.animations.idleDefault)
        //             {
        //                 this.animations.idleDefault.fadeIn(0.4)  // Smooth fade back
        //             }
        //             
        //             // Reset timer for next glance
        //             this.animations.idleTimer = 0
        //             this.animations.idleInterval = 5000 + Math.random() * 1000
        //         }
        //         else
        //         {
        //             // Glance is playing, maintain its weight
        //             this.animations.currentGlance.setEffectiveWeight(1)
        //         }
        //     }
        //     else
        //     {
        //         // Not glancing, update timer for next glance
        //         this.animations.idleTimer += this.time.delta

        //         if(this.animations.idleTimer >= this.animations.idleInterval && 
        //            this.animations.idleGlances.length > 0)
        //         {
        //             // Time for a quick glance
        //             const randomGlance = this.animations.idleGlances[
        //                 Math.floor(Math.random() * this.animations.idleGlances.length)
        //             ]

        //             if(randomGlance)
        //             {
        //                 this.animations.isGlancing = true
        //                 this.animations.glanceStartTime = this.time.elapsed
        //                 this.animations.currentGlance = randomGlance
        //                 
        //                 // Smoothly fade in the glance
        //                 randomGlance.reset()
        //                 randomGlance.fadeIn(0.4)  // Smooth fade in
        //                 randomGlance.play()
        //                 randomGlance.setEffectiveWeight(1)
        //                 
        //                 console.log('Quick glance:', randomGlance.getClip().name)
        //             }
        //         }
        //     }
        // }
        // else
        // {
        //     // When steering, reduce idle animation weights to allow steering to show
        //     if(this.animations.idleDefault && this.animations.idleDefault.isRunning())
        //     {
        //         this.animations.idleDefault.setEffectiveWeight(0.3)
        //     }
        //     if(this.animations.currentGlance && this.animations.currentGlance.isRunning())
        //     {
        //         this.animations.currentGlance.setEffectiveWeight(0.3)
        //     }
        // }
    }

    setMovement()
    {
        this.movement = {}
        this.movement.speed = new THREE.Vector3()
        this.movement.localSpeed = new THREE.Vector3()
        this.movement.acceleration = new THREE.Vector3()
        this.movement.localAcceleration = new THREE.Vector3()
        this.movement.lastScreech = 0

        // Time tick
        this.time.on('tick', () =>
        {
            // Update animations first
            this.updateAnimations()

            // Movement
            const movementSpeed = new THREE.Vector3()
            movementSpeed.copy(this.chassis.object.position).sub(this.chassis.oldPosition)
            movementSpeed.multiplyScalar(1 / this.time.delta * 17)
            this.movement.acceleration = movementSpeed.clone().sub(this.movement.speed)
            this.movement.speed.copy(movementSpeed)

            this.movement.localSpeed = this.movement.speed.clone().applyAxisAngle(new THREE.Vector3(0, 0, 1), - this.chassis.object.rotation.z)
            this.movement.localAcceleration = this.movement.acceleration.clone().applyAxisAngle(new THREE.Vector3(0, 0, 1), - this.chassis.object.rotation.z)

            // Sound
            this.sounds.engine.speed = this.movement.localSpeed.x
            this.sounds.engine.acceleration = this.controls.actions.up ? (this.controls.actions.boost ? 1 : 0.5) : 0

            // Disable screech / drift sound (kept here for potential future use)
            // if(this.movement.localAcceleration.x > 0.03 && this.time.elapsed - this.movement.lastScreech > 5000)
            // {
            //     this.movement.lastScreech = this.time.elapsed
            //     this.sounds.play('screech')
            // }
        })
    }

    setChassis()
    {
        this.chassis = {}
        this.chassis.offset = new THREE.Vector3(0, 0, - 0.28)
        
        // Don't clone! Use the original scene directly so animations work
        this.chassis.object = this.models.chassis.scene
        
        // Make sure all meshes in the hierarchy are visible
        this.chassis.object.traverse((child) => {
            if(child.isMesh)
            {
                child.visible = true
                child.castShadow = true
                child.receiveShadow = true
                
                // Apply materials if needed (optional - remove if you want to keep Blender materials)
                // child.material = this.materials.yourMaterial
            }
        })
        
        this.chassis.object.position.copy(this.physics.car.chassis.body.position)
        this.chassis.oldPosition = this.chassis.object.position.clone()
        this.container.add(this.chassis.object)

        this.shadows.add(this.chassis.object, { sizeX: 3, sizeY: 2, offsetZ: 0.2 })

        // NOW setup the animation mixer on this object
        this.setupAnimationMixer()

        // Time tick
        this.time.on('tick', () =>
        {
            // Save old position for movement calculation
            this.chassis.oldPosition = this.chassis.object.position.clone()

            // Update if mode physics
            if(!this.transformControls.enabled)
            {
                this.chassis.object.position.copy(this.physics.car.chassis.body.position).add(this.chassis.offset)
                this.chassis.object.quaternion.copy(this.physics.car.chassis.body.quaternion)
            }

            // Update position
            this.position.copy(this.chassis.object.position)
        })
    }

    setAntena()
    {
        this.antena = {}

        this.antena.speedStrength = 10
        this.antena.damping = 0.035
        this.antena.pullBackStrength = 0.02

        this.antena.object = this.objects.getConvertedMesh(this.models.antena.scene.children)
        this.chassis.object.add(this.antena.object)

        this.antena.speed = new THREE.Vector2()
        this.antena.absolutePosition = new THREE.Vector2()
        this.antena.localPosition = new THREE.Vector2()

        // Time tick
        this.time.on('tick', () =>
        {
            const max = 1
            const accelerationX = Math.min(Math.max(this.movement.acceleration.x, - max), max)
            const accelerationY = Math.min(Math.max(this.movement.acceleration.y, - max), max)

            this.antena.speed.x -= accelerationX * this.antena.speedStrength
            this.antena.speed.y -= accelerationY * this.antena.speedStrength

            const position = this.antena.absolutePosition.clone()
            const pullBack = position.negate().multiplyScalar(position.length() * this.antena.pullBackStrength)
            this.antena.speed.add(pullBack)

            this.antena.speed.x *= 1 - this.antena.damping
            this.antena.speed.y *= 1 - this.antena.damping

            this.antena.absolutePosition.add(this.antena.speed)

            this.antena.localPosition.copy(this.antena.absolutePosition)
            this.antena.localPosition.rotateAround(new THREE.Vector2(), - this.chassis.object.rotation.z)

            this.antena.object.rotation.y = this.antena.localPosition.x * 0.1
            this.antena.object.rotation.x = this.antena.localPosition.y * 0.1
        })

        // Debug
        if(this.debug)
        {
            const folder = this.debugFolder.addFolder('antena')
            folder.open()

            folder.add(this.antena, 'speedStrength').step(0.001).min(0).max(50)
            folder.add(this.antena, 'damping').step(0.0001).min(0).max(0.1)
            folder.add(this.antena, 'pullBackStrength').step(0.0001).min(0).max(0.1)
        }
    }

    setBackLights()
    {
        this.backLightsBrake = {}

        this.backLightsBrake.material = this.materials.pures.items.red.clone()
        this.backLightsBrake.material.transparent = true
        this.backLightsBrake.material.opacity = 0.5

        this.backLightsBrake.object = this.objects.getConvertedMesh(this.models.backLightsBrake.scene.children)
        for(const _child of this.backLightsBrake.object.children)
        {
            _child.material = this.backLightsBrake.material
        }

        this.chassis.object.add(this.backLightsBrake.object)

        // Back lights brake
        this.backLightsReverse = {}

        this.backLightsReverse.material = this.materials.pures.items.yellow.clone()
        this.backLightsReverse.material.transparent = true
        this.backLightsReverse.material.opacity = 0.5

        this.backLightsReverse.object = this.objects.getConvertedMesh(this.models.backLightsReverse.scene.children)
        for(const _child of this.backLightsReverse.object.children)
        {
            _child.material = this.backLightsReverse.material
        }

        this.chassis.object.add(this.backLightsReverse.object)

        // Time tick
        this.time.on('tick', () =>
        {
            this.backLightsBrake.material.opacity = this.physics.controls.actions.brake ? 1 : 0.5
            this.backLightsReverse.material.opacity = this.physics.controls.actions.down ? 1 : 0.5
        })
    }

    setWheels()
    {
        this.wheels = {}
        this.wheels.object = this.objects.getConvertedMesh(this.models.wheel.scene.children)
        this.wheels.items = []

        for(let i = 0; i < 4; i++)
        {
            const object = this.wheels.object.clone()

            this.wheels.items.push(object)
            this.container.add(object)
        }

        // Time tick
        this.time.on('tick', () =>
        {
            if(!this.transformControls.enabled)
            {
                for(const _wheelKey in this.physics.car.wheels.bodies)
                {
                    const wheelBody = this.physics.car.wheels.bodies[_wheelKey]
                    const wheelObject = this.wheels.items[_wheelKey]

                    wheelObject.position.copy(wheelBody.position)
                    wheelObject.quaternion.copy(wheelBody.quaternion)
                }
            }
        })
    }

    setTransformControls()
    {
        this.transformControls = new TransformControls(this.camera.instance, this.renderer.domElement)
        this.transformControls.size = 0.5
        this.transformControls.attach(this.chassis.object)
        this.transformControls.enabled = false
        this.transformControls.visible = this.transformControls.enabled

        document.addEventListener('keydown', (_event) =>
        {
            if(this.mode === 'transformControls')
            {
                if(_event.key === 'r')
                {
                    this.transformControls.setMode('rotate')
                }
                else if(_event.key === 'g')
                {
                    this.transformControls.setMode('translate')
                }
            }
        })

        this.transformControls.addEventListener('dragging-changed', (_event) =>
        {
            this.camera.orbitControls.enabled = !_event.value
        })

        this.container.add(this.transformControls)

        if(this.debug)
        {
            const folder = this.debugFolder.addFolder('controls')
            folder.open()

            folder.add(this.transformControls, 'enabled').onChange(() =>
            {
                this.transformControls.visible = this.transformControls.enabled
            })
        }
    }

    setShootingBall()
    {
        if(!this.config.cyberTruck)
        {
            return
        }

        window.addEventListener('keydown', (_event) =>
        {
            if(_event.key === 'b')
            {
                const angle = Math.random() * Math.PI * 2
                const distance = 10
                const x = this.position.x + Math.cos(angle) * distance
                const y = this.position.y + Math.sin(angle) * distance
                const z = 2 + 2 * Math.random()
                const bowlingBall = this.objects.add({
                    base: this.resources.items.bowlingBallBase.scene,
                    collision: this.resources.items.bowlingBallCollision.scene,
                    offset: new THREE.Vector3(x, y, z),
                    rotation: new THREE.Euler(Math.PI * 0.5, 0, 0),
                    duplicated: true,
                    shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.15, alpha: 0.35 },
                    mass: 5,
                    soundName: 'bowlingBall',
                    sleep: false
                })

                const carPosition = new CANNON.Vec3(this.position.x, this.position.y, this.position.z + 1)
                let direction = carPosition.vsub(bowlingBall.collision.body.position)
                direction.normalize()
                direction = direction.scale(100)
                bowlingBall.collision.body.applyImpulse(direction, bowlingBall.collision.body.position)
            }
        })
    }

    setKlaxon()
    {
        this.klaxon = {}
        this.klaxon.lastTime = this.time.elapsed

        window.addEventListener('keydown', (_event) =>
        {
            // Play horn sound
            if(_event.code === 'KeyH')
            {
                if(this.time.elapsed - this.klaxon.lastTime > 400)
                {
                    this.physics.car.jump(false, 150)
                    this.klaxon.lastTime = this.time.elapsed
                }

                this.sounds.play(Math.random() < 0.002 ? 'carHorn2' : 'carHorn1')
            }

            // Rain horns
            if(_event.key === 'k')
            {
                const x = this.position.x + (Math.random() - 0.5) * 3
                const y = this.position.y + (Math.random() - 0.5) * 3
                const z = 6 + 2 * Math.random()

                this.objects.add({
                    base: this.resources.items.hornBase.scene,
                    collision: this.resources.items.hornCollision.scene,
                    offset: new THREE.Vector3(x, y, z),
                    rotation: new THREE.Euler(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2),
                    duplicated: true,
                    shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.15, alpha: 0.35 },
                    mass: 5,
                    soundName: 'horn',
                    sleep: false
                })
            }
        })
    }

    setSmoke()
    {
        this.smoke = {}
        this.smoke.points = []
        
        // Exhaust points (where smoke starts) - 2 points
        const exhaustNames = [
            'Exhaust1', 'Exhaust2',
            'SmokePoint1', 'SmokePoint2',
            'Smoke_1', 'Smoke_2'
        ]
        
        // Direction points (where smoke should point) - 2 points
        const directionNames = [
            'Direction1', 'Direction2',
            'SmokeDirection1', 'SmokeDirection2',
            'Dir1', 'Dir2'
        ]
        
        const exhaustPoints = {}
        const directionPoints = {}
        
        // Traverse the car model to find exhaust and direction points
        this.chassis.object.traverse((child) => {
            // Find exhaust points
            if(exhaustNames.includes(child.name))
            {
                exhaustPoints[child.name] = child
                console.log(`Found exhaust point: ${child.name} at position:`, child.position)
            }
            // Find direction points
            if(directionNames.includes(child.name))
            {
                directionPoints[child.name] = child
                console.log(`Found direction point: ${child.name} at position:`, child.position)
            }
        })
        
        // Match exhaust points with direction points and create smoke
        // Exhaust1 -> Direction1, Exhaust2 -> Direction2, etc.
        Object.keys(exhaustPoints).forEach((exhaustName) => {
            // Extract number from exhaust name (e.g., "Exhaust1" -> "1")
            const match = exhaustName.match(/(\d+)$/)
            if(!match) return
            
            const number = match[1]
            
            // Find corresponding direction point
            let directionName = null
            let directionPoint = null
            
            // Try different naming patterns
            for(const dirName of directionNames)
            {
                if(dirName.includes(number))
                {
                    directionName = dirName
                    directionPoint = directionPoints[dirName]
                    break
                }
            }
            
            const exhaustPoint = exhaustPoints[exhaustName]
            
            if(!directionPoint)
            {
                console.warn(`No direction point found for ${exhaustName}. Smoke will use default direction.`)
                // Use exhaust point's rotation as fallback
                this.smoke[exhaustName] = new Smoke({
                    time: this.time,
                    scene: null,
                    debug: this.debug,
                    parent: this.chassis.object,
                    offset: exhaustPoint.position.clone(),
                    rotation: exhaustPoint.rotation.clone()
                })
            }
            else
            {
                // Calculate direction from exhaust to direction point
                const direction = new THREE.Vector3()
                direction.subVectors(directionPoint.position, exhaustPoint.position).normalize()
                
                // Convert direction vector to rotation (Euler)
                // Create a look-at matrix to get rotation
                const up = new THREE.Vector3(0, 1, 0)
                const quaternion = new THREE.Quaternion()
                const matrix = new THREE.Matrix4()
                matrix.lookAt(new THREE.Vector3(0, 0, 0), direction, up)
                quaternion.setFromRotationMatrix(matrix)
                const rotation = new THREE.Euler().setFromQuaternion(quaternion)
                
                console.log(`Creating smoke: ${exhaustName} -> ${directionName}`)
                console.log(`  Exhaust position:`, exhaustPoint.position)
                console.log(`  Direction position:`, directionPoint.position)
                console.log(`  Calculated rotation:`, rotation)
                
                this.smoke[exhaustName] = new Smoke({
                    time: this.time,
                    scene: null,
                    debug: this.debug,
                    parent: this.chassis.object,
                    offset: exhaustPoint.position.clone(),
                    rotation: rotation
                })
            }
            
            this.smoke.points.push(this.smoke[exhaustName])
        })
        
        if(Object.keys(exhaustPoints).length === 0)
        {
            console.warn('No exhaust points found. Create empty objects named: Exhaust1, Exhaust2 (or SmokePoint1, SmokePoint2) in Blender.')
        }
        else
        {
            console.log(`Created ${Object.keys(exhaustPoints).length} smoke effect(s)`)
        }
    }
}